import type { EnumDef, FieldDef, TypeDef } from '../types/model';
import type { EnumSchema, JsonSchema, ObjectSchema, SchemaProperty } from '../types/schema';
import { buildFieldSchema } from './schemaUtils';

const REF_PREFIX = '#/$defs/';

const buildProperties = (fields: FieldDef[]): { properties: Record<string, SchemaProperty>; required: string[] } => {
  const properties: Record<string, SchemaProperty> = {};
  const required: string[] = [];
  fields.forEach((field) => {
    if (!field.name.trim()) {
      return;
    }
    properties[field.name] = buildFieldSchema(field);
    if (field.isRequired) {
      required.push(field.name);
    }
  });
  return { properties, required };
};

export const buildTypeSchema = (type: TypeDef): ObjectSchema => {
  const { properties, required } = buildProperties(type.fields);
  const base = {
    title: type.name,
    description: type.description,
    'x-indexed': type.isIndexed,
  };
  if (type.parentTypeId) {
    return {
      ...base,
      allOf: [
        { $ref: `${REF_PREFIX}${type.parentTypeId}` },
        { type: 'object', properties, required },
      ],
    };
  }
  return {
    ...base,
    type: 'object',
    properties,
    required,
  };
};

export const buildEnumSchema = (entry: EnumDef): EnumSchema => ({
  type: 'string',
  title: entry.name,
  description: entry.description,
  oneOf: entry.values.map((value) => ({ const: value.id, title: value.label })),
});

export const upsertTypeDef = (schema: JsonSchema, type: TypeDef): JsonSchema => {
  const defs = { ...(schema.$defs ?? {}) };
  defs[type.id] = buildTypeSchema(type);
  return {
    ...schema,
    $defs: defs,
  };
};

export const upsertEnumDef = (schema: JsonSchema, entry: EnumDef): JsonSchema => {
  const defs = { ...(schema.$defs ?? {}) };
  defs[entry.id] = buildEnumSchema(entry);
  return {
    ...schema,
    $defs: defs,
  };
};

export const removeDef = (schema: JsonSchema, defId: string): JsonSchema => {
  const defs = { ...(schema.$defs ?? {}) };
  delete defs[defId];
  return {
    ...schema,
    $defs: defs,
  };
};
